// My scorecard (KPIs › My numbers › Scorecard tab, `/c/{slug}/kpis/my-numbers?tab=scorecard`; UX-A
// moved it from `/kpis/my-scorecard`, which redirects; MEMBER-ENTRY, founder-approved 2026-10-21;
// spec 13 §Screens #12): a member's own seats' scorecard rows — Cadence's one door open to a plain
// member (05: scorecard viewer = seat#holder). Per held seat ("Seat · Holder"): its band and KPI
// score, then each metric with its goal, an 8-period sparkline and the last 8 periods as cells
// tinted hit or missed (the company grid's `gridCell`), by weeks, months or quarters. The seats
// and the KPI audience come from `GET me/numbers`; each card is `GET seats/{id}/scorecard`
// (`seat#can_view`). The chart's dimension audience is respected: when the company's KPI
// audience (`settings.chart.visibility.kpi`) does not reach the holder, the band, the score and
// the hit/miss tints are left out and the rows show plain values.
import {
  BandBadge,
  EmptyState,
  InfoTip,
  ScoreCell,
  Segmented,
  Sparkline,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import { GaugeIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { Scorecard } from "../../api/types";
import type { CompanyAccess } from "../company";
import { gridCell } from "../company/scorecard-grid";
import { trendLabel } from "../de/scorecard-tab";
import { companyPeriodNow } from "../entry/calendar";
import { formatMetricValue, formatScore, formatTarget, seatHolderLabel } from "../format";
import { myNumbersQuery, seatPeriodScorecardQuery } from "../queries";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import type { MySeat } from "./my-numbers-model";

type Grain = "week" | "month" | "quarter";
const GRAINS: { value: Grain; label: string }[] = [
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
  { value: "quarter", label: "Quarters" },
];

/** A column heading: "W42", "Oct", "Q4". */
export function columnLabel(grain: Grain, key: string): string {
  if (grain === "week") return `W${key.slice(6)}`;
  if (grain === "quarter") return key.slice(5);
  const m = Number(key.slice(5, 7));
  return (
    ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] ??
    key
  );
}

/** The grid's columns: every period of the seat's trends, oldest first. */
export function columnsOf(card: Pick<Scorecard, "metrics" | "period">): string[] {
  const keys = new Set<string>([card.period.key]);
  for (const m of card.metrics) for (const t of m.trend) keys.add(t.period_key);
  return [...keys].sort().slice(-8);
}

export function MyScorecardScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const numbers = useQuery(myNumbersQuery(api, company.id));
  const focusedWeek = usePeriod();
  const [grain, setGrain] = useState<Grain>("week");
  const period =
    grain === "week" ? focusedWeek : companyPeriodNow(company.calendar, grain, new Date());
  const seats = numbers.data?.seats ?? [];
  const cards = useQueries({
    queries: seats.map((s) => ({
      ...seatPeriodScorecardQuery(api, company.id, s.seat_id, grain, period),
      retry: false,
    })),
  });
  const currency = numbers.data?.currency ?? "USD";
  return (
    <div data-slot="my-scorecard" className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <Segmented<Grain>
          aria-label="Periods"
          size="sm"
          options={GRAINS}
          value={grain}
          onValueChange={setGrain}
        />
        <InfoTip label="About my scorecard">
          Your seats' KPIs: each period hit or missed against its goal.
        </InfoTip>
      </div>
      {numbers.isPending ? <Loading label="Loading your scorecard" /> : null}
      {numbers.isError ? <LoadError what="Your seats" error={numbers.error} /> : null}
      {numbers.data && seats.length === 0 ? (
        <EmptyState
          variant="card"
          icon={GaugeIcon}
          title="No scorecard yet"
          description="Your scorecard shows here once you hold a seat with KPIs."
        />
      ) : null}
      {seats.map((seat, i) => {
        const q = cards[i];
        return (
          <SeatGrid
            key={seat.seat_id}
            seat={seat}
            grain={grain}
            card={q?.data}
            error={q?.error ?? null}
            currency={currency}
          />
        );
      })}
    </div>
  );
}

function SeatGrid({
  seat,
  grain,
  card,
  error,
  currency,
}: {
  seat: MySeat;
  grain: Grain;
  card: Scorecard | undefined;
  error: unknown;
  currency: string;
}) {
  const label = seatHolderLabel(seat.title, seat.holder_name);
  const health = seat.kpi_visible;
  if (error) return <LoadError what={`The scorecard of ${label}`} error={error} />;
  if (!card) return <Loading label={`Loading ${label}`} rows={2} />;
  const cols = columnsOf(card);
  const metrics = card.metrics.filter((m) => m.tracking_mode !== "off");
  return (
    <section data-seat={seat.seat_id} aria-label={label} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-sm font-semibold text-foreground">{label}</h2>
        {health ? (
          <span className="ml-auto flex items-center gap-2" data-slot="seat-health">
            <BandBadge band={card.band} />
            <span className="text-sm text-foreground tabular-nums">
              {card.kpi_score === null ? "—" : formatScore(card.kpi_score)}
            </span>
          </span>
        ) : (
          <span className="ml-auto text-xs text-muted-foreground">
            KPI health is shared with managers only
          </span>
        )}
      </div>
      {metrics.length === 0 ? (
        <p className="text-sm text-muted-foreground">No KPI on this seat yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table className="min-w-[48rem] table-fixed" data-slot="my-scorecard-grid">
            <TableCaption className="sr-only">
              {label}: each KPI over the last {cols.length} periods, oldest first.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="w-56">
                  Measurable
                </TableHead>
                <TableHead scope="col" className="w-20 text-right">
                  Goal
                </TableHead>
                <TableHead scope="col" className="w-24 max-md:hidden">
                  Trend
                </TableHead>
                {cols.map((k) => (
                  <TableHead
                    key={k}
                    scope="col"
                    data-period={k}
                    data-current={k === card.period.key || undefined}
                    className="w-16 px-2 text-center tabular-nums data-current:text-primary-text"
                  >
                    {columnLabel(grain, k)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.map((m) => (
                <TableRow key={m.metric_def_id} data-metric={m.key}>
                  <TableHead scope="row" className="truncate font-medium">
                    {m.name}
                  </TableHead>
                  <TableCell className="whitespace-nowrap text-right text-muted-foreground tabular-nums">
                    {formatTarget(m, currency)}
                  </TableCell>
                  <TableCell className="max-md:hidden">
                    <Sparkline
                      label={trendLabel(m, currency, card.period.key)}
                      values={m.trend.map((p) => p.actual)}
                      reference={m.trend.map((p) => p.target)}
                    />
                  </TableCell>
                  {cols.map((k) => {
                    const cell = gridCell(m, k, card.period.key, currency);
                    const point = m.trend.find((t) => t.period_key === k);
                    const value =
                      point?.actual == null
                        ? null
                        : formatMetricValue(point.actual, m.unit, currency);
                    return (
                      <TableCell key={k} data-period={k} className="w-16 p-0 align-middle">
                        <ScoreCell
                          state={health ? cell.state : "none"}
                          value={health ? cell.value : value}
                          label={health ? cell.label : (value ?? "No data")}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
